import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    TiktokAudioCollection,
    getCollection,
    getTiktokAudioCollectionTiktokAudioRelationships,
    TiktokAudioCollectionTiktokAudioRelationship,
    postTiktokAudioCollectionTiktokAudioRelationship,
    deleteTiktokAudioCollectionTiktokAudioRelationship,
} from '@round/api';
import { ExploreTiktokAudiosTableRow, ExploreTiktokAudiosTableRowV2 } from '../../../../../../TikTok.types';
import { Popover } from '@round/ui-kit';
import { useCheckUserGroupsAccess } from '../../../../../../../Auth/hooks/useCheckUserGroupsAccess';
import styles from './CollectionsCell.module.css';
import OptionsList from '../../../../../../../../ui/DataEntry/SelectBox/components/OptionsList/OptionsList';
import Image from '../../../../../../../../ui/Image/Image';
import Skeleton from '../../../../../../../../ui/Skeleton/Skeleton';
import Checkbox from '../../../../../../../../ui/DataEntry/Checkbox/Checkbox';
import Card from '../../../../../../../../ui/Card/Card';
import cn from 'classnames';
import useNonNullContext from '../../../../../../../../Hooks/useNonNullContext';
import { CollectionsCellContext } from './CollectionsCellContext';
import { showNotification } from '../../../../../../../../helpers';
import { formatAudioCount } from '../../../../TiktokAudioCollections/helpers';
import uniqBy from 'lodash/uniqBy';
import usePrevious from '../../../../../../../../Hooks/usePrevious';

type CollectionsCellProps = {
    onAudioAddedToCollection: (relation: TiktokAudioCollectionTiktokAudioRelationship) => void;
    onAudioRemovedFromCollection: (relation: TiktokAudioCollectionTiktokAudioRelationship) => void;
} & (RowLoaded | RowLoading);

type RowLoading = { loading: true; row: Partial<ExploreTiktokAudiosTableRow | ExploreTiktokAudiosTableRowV2> };
type RowLoaded = { loading: false; row: ExploreTiktokAudiosTableRow | ExploreTiktokAudiosTableRowV2 };

const CollectionsCell = (props: CollectionsCellProps) => {
    const prevLinkedSongs = usePrevious(props.row.linkedSongs);
    const hasCollectionEditorPermissions = useCheckUserGroupsAccess(['catalogue_editor']);
    const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);
    const {
        loadMoreCollections,
        collectionsLoading,
        collectionsCount,
        collections,
        setCollectionsSearchQuery,
        collectionsSearchQuery,
    } = useNonNullContext(CollectionsCellContext);
    const [initialCollectionIds, setInitialCollectionIds] = useState<number[]>([]);
    const [collectionTiktokAudioRelations, setCollectionTiktokAudioRelations] = useState<
        TiktokAudioCollectionTiktokAudioRelationship[]
    >([]);
    const [initialCollections, setInitialCollections] = useState<TiktokAudioCollection[]>([]);
    const collectionOptions = useMemo(
        () =>
            uniqBy(collections.concat(initialCollections), (collection) => collection.id).map((c) => ({
                id: c.id,
                subtitle: formatAudioCount(c.tiktok_audio_count),
                picture: c.picture ?? '',
                name: c.name,
            })),
        [collections, initialCollections]
    );

    const sortedCollectionOptions = useMemo(
        () =>
            Array.from(collectionOptions).sort((a, b) => {
                return initialCollectionIds.indexOf(b.id) - initialCollectionIds.indexOf(a.id);
            }),
        [collectionOptions, initialCollectionIds]
    );

    const fetchCollections = useCallback(async (ids: number[]) => {
        if (!ids.length) {
            return;
        }

        try {
            const responses = await Promise.all(ids.map((id) => getCollection(id)));
            const collections = responses.reduce<TiktokAudioCollection[]>((acc, response) => {
                if (response.status === 404) {
                    return acc;
                }

                return acc.concat(response.data);
            }, []);

            setInitialCollections(collections);
        } catch {
            // no-op
        }
    }, []);

    const fetchCollectionTiktokAudioRelations = useCallback(async (musicId: number) => {
        try {
            const paginatedResponse = await getTiktokAudioCollectionTiktokAudioRelationships({ music_id: musicId });
            setCollectionTiktokAudioRelations(paginatedResponse.data.results);
        } catch {
            // no-op
        }
    }, []);

    const handleAddAudio = useCallback(
        async (audioId: number, collectionId: number) => {
            try {
                const response = await postTiktokAudioCollectionTiktokAudioRelationship({
                    music: audioId,
                    catalogue: collectionId,
                });

                if (response.status === 201) {
                    setCollectionTiktokAudioRelations((relations) => relations.concat(response.data));
                    showNotification('Audio added', 'info');
                    props.onAudioAddedToCollection(response.data);
                }
            } catch {
                showNotification('Could not add audio to collection', 'error');
            }
        },
        [props]
    );

    const handleRemoveAudio = useCallback(
        async (relation: TiktokAudioCollectionTiktokAudioRelationship) => {
            try {
                const response = await deleteTiktokAudioCollectionTiktokAudioRelationship(relation.id);
                if (response.status === 204) {
                    showNotification('Audio removed', 'info');
                    setCollectionTiktokAudioRelations((relations) => relations.filter((rel) => rel.id !== relation.id));
                    props.onAudioRemovedFromCollection(relation);
                }
            } catch {
                showNotification('Could not remove audio from collection', 'error');
            }
        },
        [props]
    );

    const handleShowPopoverChange = useCallback(
        async (show: boolean) => {
            if (props.loading) {
                return;
            }

            if (show) {
                setInitialCollectionIds(props.row.catalogue_ids);
                await fetchCollections(props.row.catalogue_ids);
                return fetchCollectionTiktokAudioRelations(props.row.music_id);
            }

            const collectionsChanged =
                initialCollectionIds.join() !== collectionTiktokAudioRelations.map((r) => r.catalogue).join();
            if (!show && collectionsChanged) {
                setInitialCollectionIds(collectionTiktokAudioRelations.map((r) => r.catalogue));
            }
        },
        [
            collectionTiktokAudioRelations,
            fetchCollectionTiktokAudioRelations,
            fetchCollections,
            initialCollectionIds,
            props.loading,
            props.row.catalogue_ids,
            props.row.music_id,
        ]
    );

    useEffect(() => {
        if (Array.isArray(props.row.catalogue_ids)) {
            setInitialCollectionIds(props.row.catalogue_ids);
        }
    }, [props.row.catalogue_ids]);

    useEffect(() => {
        const linkedSongsHaveChanged =
            Array.isArray(prevLinkedSongs) &&
            prevLinkedSongs.map((s) => s.id).join() !== (props.row.linkedSongs ?? []).map((s) => s.id).join();

        if (!props.loading && linkedSongsHaveChanged) {
            getTiktokAudioCollectionTiktokAudioRelationships({ music_id: props.row.music_id })
                .then((response) => {
                    setInitialCollectionIds(response.data.results.map((r) => r.catalogue));
                })
                .catch(() => {});
        }
    }, [prevLinkedSongs, props.loading, props.row.linkedSongs, props.row.music_id]);

    if (props.loading) {
        return <Skeleton />;
    }

    const { row } = props;
    return (
        <div
            onClick={(e) => {
                if (hasCollectionEditorPermissions) {
                    e.stopPropagation();
                }
            }}
        >
            <button
                className={cn(styles.audioCollectionsButton, { [styles.clickable]: hasCollectionEditorPermissions })}
                ref={setButtonRef}
            >
                Added to {initialCollectionIds.length} collection{initialCollectionIds.length !== 1 ? 's' : ''}
            </button>
            <Popover
                disabled={!hasCollectionEditorPermissions}
                anchorElement={buttonRef}
                showOn="click"
                options={{ placement: 'bottom-end' }}
                onShowPopoverChange={handleShowPopoverChange}
            >
                <Card className={styles.collectionsDropdown}>
                    <OptionsList
                        searchable
                        searchQuery={collectionsSearchQuery}
                        setSearchQuery={setCollectionsSearchQuery}
                        searchPlaceholder="Search collections"
                        options={sortedCollectionOptions}
                        count={collectionsCount}
                        loading={collectionsLoading}
                        loadMore={loadMoreCollections}
                    >
                        {({ item, style, loading }) => {
                            const addedToCollection =
                                typeof item?.id === 'number' && row.catalogue_ids.includes(item.id);
                            const collectionTiktokAudioRelation = collectionTiktokAudioRelations.find(
                                (rel) => rel.music === row.music_id && rel.catalogue === item?.id
                            );

                            return (
                                <div
                                    className={styles.collectionOption}
                                    style={style}
                                    onClick={() => {
                                        if (typeof item?.id !== 'number') {
                                            return;
                                        }

                                        if (!addedToCollection) {
                                            return handleAddAudio(row.music_id, item.id);
                                        }

                                        if (collectionTiktokAudioRelation) {
                                            return handleRemoveAudio(collectionTiktokAudioRelation);
                                        }
                                    }}
                                >
                                    <Image
                                        className={styles.collectionOptionImage}
                                        src={item?.picture}
                                        loading={loading}
                                    />
                                    <div className={styles.collectionOptionNamesContainer}>
                                        <p className={styles.collectionOptionName}>
                                            {loading ? <Skeleton /> : item?.name}
                                        </p>
                                        <p className={styles.collectionOptionSubtitle}>
                                            {loading ? <Skeleton /> : item?.subtitle}
                                        </p>
                                    </div>
                                    <Checkbox value={addedToCollection} />
                                </div>
                            );
                        }}
                    </OptionsList>
                </Card>
            </Popover>
        </div>
    );
};

export default CollectionsCell;
